import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dimmer, Loader, Popup, SemanticICONS } from 'semantic-ui-react'
import CustomIcon from '~/components/CustomIcon'
import i18n from '~/components/i18n'
import { ReportingQuery } from '~/QueryHooks/ReportingQuery'
import { DataFilteredType } from '.'
import { DownloadService } from '~/api/services/downloadFiles'
import { handleErrors } from '~/utils/helpers'
import TableComponent from '~/components/TableComponent'
import PreviewModal from './PreviewModal'

const getDefaultHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName',
    sorted: 'desc'
  },
  {
    name: i18n.t('Completed at'),
    key: 'completedAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: 'THG Emissionen (kg CO<sub>2</sub>-Äq./Kuh u. Jahr)',
    key: 'value',
    type: 'html-header',
    sorted: 'asc'
  },
  {
    name: i18n.t('Actions')
  }
]

interface Props {
  dataFiltered: DataFilteredType
}

const CO2Footprint: React.FC<Props> = (props) => {
  const { t } = useTranslation()

  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    ...props.dataFiltered
  })

  const {
    data: co2Footprints,
    isLoading: isLoading,
    isFetching: isFetching,
    refetch: fetchData
  } = ReportingQuery.climaFootprint(dataFiltered)

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultHeaders())
  const [uniqueId, setUniqueId] = useState(null)
  const [farmerId, setFarmerId] = useState(null)
  const [previewModalId, setPreviewModalId] = useState(null)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: 'climateCheckFootprintId'
  })

  const actions = [
    {
      popupMessage: t('Show'),
      name: 'eye',
      type: 'icon',
      actionType: 'show',
      renderActions: getActions
    },
    {
      popupMessage: <span dangerouslySetInnerHTML={{ __html: t('CO2 emission result') }} />,
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'pdf-results',
      renderActions: getActions
    }
  ]

  useEffect(() => {
    const newDataFiltered = {
      ...dataFiltered,
      ...props.dataFiltered
    }
    if (JSON.stringify(dataFiltered) !== JSON.stringify(newDataFiltered)) {
      setDataFiltered((prevData) => (
        {
          ...prevData,
          ...props.dataFiltered
        }
      ))
    }
  }, [props.dataFiltered])

  useEffect(() => {
    setTableColumnHeaders(getDefaultHeaders())
  }, [t])

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  async function handleDownloadPdfResults(row) {
    setLoadingAction({
      type: 'pdf-results',
      id: row.climateCheckFootprintId,
      value: true,
      idKey: 'climateCheckFootprintId'
    })
    try {
      await DownloadService.downloadFileWithName(`farmers/${row.farmerId}/climate-check-footprint/results/${row.climateCheckFootprintId}/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleShow(row) {
    setPreviewModalId(row?.climateCheckFootprintId)
    setFarmerId(row?.farmerId)
    setUniqueId(row?.uniqueId)
  }

  function getActions(row, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    switch (actionType) {
      case 'pdf-results':
        return <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handleDownloadPdfResults(row)}
            size='large'
          />}
        />
      case 'show':
        return <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }}
            onClick={() => handleShow(row)}
            name={action.name as SemanticICONS}
            size='large'
          />}
        />
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetching}
      actions={actions}
      loadingAction={loadingAction}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setDataFiltered(data)
        setTableColumnHeaders(tableColumnHeaders)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderPreviewModal() {
    return <PreviewModal
      id={previewModalId}
      handleClose={() => {
        setPreviewModalId(null)
        setUniqueId(null)
        setFarmerId(null)
      }}
      uniqueId={uniqueId}
      farmerId={farmerId}
    />
  }

  const { filtered, rows } = co2Footprints ? co2Footprints : { filtered: 0, rows: [] };

  return <div style={{ position: 'relative', marginTop: '2em' }}>
    <Dimmer inverted active={isLoading}>
      <Loader active={isLoading} />
    </Dimmer>
    {renderPreviewModal()}
    {renderTable()}
  </div>
}
export default CO2Footprint